import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import styled from "styled-components";
import {IStoreState} from "modules/types";
import {IS_MX} from "modules/utils";
import {below} from "assets/css/media";
import {Overlay} from "components/Overlay";
import {Close} from "components/Icons/Close";

const VIDEO_URL = process.env.REACT_APP_AD_VIDEO_URL || "";
const VIDEO_LINK = process.env.REACT_APP_AD_VIDEO_LINK || "";
const VIDEO_AD_STORAGE_KEY = "video_ad_viewed";

const PopupWrapper = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 2;
	width: 100%;
	max-width: 640px;
	padding: 0 20px;
	box-sizing: border-box;

	${below.desktop`
		padding: 0 10px;
	`};
`;

const CloseButton = styled.button`
	position: absolute;
	top: -34px;
	right: 20px;
	width: 24px;
	height: 24px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;

	${below.desktop`
		right: 10px;
	`};
`;

const VideoWrapper = styled.div`
	position: relative;
	display: block;
	border-radius: 4px;
	overflow: hidden;
	line-height: 0;
	background: #000;
`;

const VideoLink = styled.a`
	display: block;
	line-height: 0;
`;

const Video = styled.video`
	width: 100%;
	height: auto;
	display: block;
`;

const isVideoAdViewed = () =>
	window.localStorage.getItem(VIDEO_AD_STORAGE_KEY) === VIDEO_URL;

const setVideoAdViewed = () =>
	window.localStorage.setItem(VIDEO_AD_STORAGE_KEY, VIDEO_URL);

interface IProps {
	onVisibilityChange?: (is_visible: boolean) => void;
}

export const VideoAdPopup = ({onVisibilityChange}: IProps) => {
	const is_logged_in = useSelector(
		(state: IStoreState) => state.user.is_logged_in
	);
	const [is_visible, setIsVisible] = useState(false);
	const video_ref = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		if (!IS_MX || !VIDEO_URL || !is_logged_in || isVideoAdViewed()) {
			return;
		}

		setIsVisible(true);
	}, [is_logged_in]);

	useEffect(() => {
		onVisibilityChange?.(is_visible);
	}, [is_visible, onVisibilityChange]);

	const handleClose = useCallback(() => {
		setVideoAdViewed();
		setIsVisible(false);
	}, []);

	useEffect(() => {
		if (!is_visible) {
			return;
		}

		const play_promise = video_ref.current?.play();

		if (play_promise) {
			play_promise.catch(() => undefined);
		}
	}, [is_visible]);

	if (!is_visible) {
		return null;
	}

	const video = (
		<Video
			ref={video_ref}
			src={VIDEO_URL}
			autoPlay={true}
			muted={true}
			playsInline={true}
			controls={false}
			onEnded={handleClose}
			onError={handleClose}
		/>
	);

	return (
		<React.Fragment>
			<Overlay />
			<PopupWrapper>
				<CloseButton onClick={handleClose} aria-label="close">
					<Close width={14} height={14} color="#ffffff" />
				</CloseButton>
				<VideoWrapper>
					{VIDEO_LINK ? (
						<VideoLink
							href={VIDEO_LINK}
							target="_blank"
							rel="noopener noreferrer"
							onClick={handleClose}
						>
							{video}
						</VideoLink>
					) : (
						video
					)}
				</VideoWrapper>
			</PopupWrapper>
		</React.Fragment>
	);
};

export default VideoAdPopup;
